import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar,
  Umbrella,
  Plus,
  Eye,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  GET_ALL_LEAVES,
  GET_ALL_EMPLOYEES,
  UPDATE_LEAVE_MUTATION,
  DELETE_LEAVE_MUTATION,
  CREATE_LEAVE_MUTATION,
  GET_SYSTEM_SETTINGS,
  GET_LEAVE_TYPES,
} from '../../graphql/queries';
import { usePermission } from '../../hooks/usePermission';
import { LeaveDialog, LeaveFormData } from './LeaveDialog';
import { LeaveDetailsDialog } from './LeaveDetailsDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { MultiSelect } from '../ui/multi-select';

interface LeaveManagementProps {
  currentUserRole: string;
  currentUserId?: string;
}

interface Leave {
  id: string;
  employeeId: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  status: string;
  reason?: string;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    department?: string;
    position?: string;
  };
}

const FALLBACK_LEAVE_TYPES = [
  { value: 'VACATION', label: 'Urlop wypoczynkowy', color: 'bg-green-100 text-green-800' },
  { value: 'SICK_LEAVE', label: 'Zwolnienie lekarskie', color: 'bg-red-100 text-red-800' },
  { value: 'PERSONAL', label: 'Urlop okolicznościowy', color: 'bg-blue-100 text-blue-800' },
  { value: 'MATERNITY', label: 'Urlop macierzyński', color: 'bg-pink-100 text-pink-800' },
  { value: 'PATERNITY', label: 'Urlop ojcowski', color: 'bg-purple-100 text-purple-800' },
  { value: 'UNPAID', label: 'Urlop bezpłatny', color: 'bg-gray-100 text-gray-800' },
  { value: 'OTHER', label: 'Inny', color: 'bg-yellow-100 text-yellow-800' },
];

const LEAVE_STATUS = [
  { value: 'PENDING', label: 'Oczekujący', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'APPROVED', label: 'Zatwierdźony', color: 'bg-green-100 text-green-800' },
  { value: 'REJECTED', label: 'Odrzućony', color: 'bg-red-100 text-red-800' },
  { value: 'CANCELLED', label: 'Anulowany', color: 'bg-gray-100 text-gray-800' },
];

const PAGE_SIZE = 15;

const getStatusConfig = (status: string) => {
  return LEAVE_STATUS.find((s) => s.value === status) || LEAVE_STATUS[0];
};

export const LeaveManagement: React.FC<LeaveManagementProps> = ({
  currentUserRole,
  currentUserId,
}) => {
  const canApprovePerm = usePermission('leaves.approve');
  const canEditPerm = usePermission('leaves.approve'); // edit-leave gated by approve permission for now
  const canDeletePerm = usePermission('leaves.delete');

  // Read URL params for pre-filtering
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const urlEmployeeId = urlParams.get('employee') || '';

  // Current month date range defaults
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filter state
  const [filterEmployeeIds, setFilterEmployeeIds] = useState<string[]>(urlEmployeeId ? [urlEmployeeId] : []);
  const [filterLeaveTypes, setFilterLeaveTypes] = useState<string[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState(formatDateForInput(firstDayOfMonth));
  const [filterDateTo, setFilterDateTo] = useState(formatDateForInput(lastDayOfMonth));

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [editFormData, setEditFormData] = useState({
    leaveType: '',
    startDate: '',
    endDate: '',
    status: '',
    reason: '',
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState<Leave | null>(null);

  // Queries
  const { data: leavesData, loading, error, refetch: refetchLeaves } = useQuery(GET_ALL_LEAVES, {
    fetchPolicy: 'network-only',
  });

  const { data: employeesData } = useQuery(GET_ALL_EMPLOYEES, {
    fetchPolicy: 'cache-first',
  });

  const { data: settingsData } = useQuery(GET_SYSTEM_SETTINGS, {
    fetchPolicy: 'cache-first',
  });

  const { data: leaveTypesData } = useQuery(GET_LEAVE_TYPES, {
    fetchPolicy: 'cache-first',
  });

  // Dynamic leave types from database with fallback
  const leaveTypes = useMemo(() => {
    const fallbackColorMap: Record<string, string> = {};
    FALLBACK_LEAVE_TYPES.forEach((t: { value: string; color: string }) => { fallbackColorMap[t.value] = t.color; });

    const dynamicTypes = (leaveTypesData?.leaveTypes || [])
      .filter((t: any) => t.isActive)
      .map((t: any) => ({
        value: t.code,
        label: t.displayName,
        color: fallbackColorMap[t.code] || 'bg-yellow-100 text-yellow-800',
      }));
    return dynamicTypes.length > 0 ? dynamicTypes : FALLBACK_LEAVE_TYPES;
  }, [leaveTypesData]);

  const getLeaveTypeConfig = (type: string) => {
    return leaveTypes.find((t: { value: string }) => t.value === type) || { value: type, label: type, color: 'bg-yellow-100 text-yellow-800' };
  };

  // Mutations
  const [createLeave] = useMutation(CREATE_LEAVE_MUTATION, {
    onCompleted: () => {
      toast.success('Nieobecność została dodana');
      refetchLeaves();
    },
    onError: (err) => {
      toast.error(err.message || 'Błąd podczas dodawania nieobecności');
    },
  });

  const [updateLeave, { loading: updateLoading }] = useMutation(UPDATE_LEAVE_MUTATION, {
    onCompleted: () => {
      refetchLeaves();
    },
    onError: (err) => {
      toast.error(err.message || 'Błąd podczas aktualizacji nieobecności');
    },
  });

  const [deleteLeave, { loading: deleteLoading }] = useMutation(DELETE_LEAVE_MUTATION, {
    onCompleted: () => {
      toast.success('Nieobecność została usunięta');
      refetchLeaves();
    },
    onError: (err) => {
      toast.error(err.message || 'Błąd podczas usuwania nieobecności');
    },
  });

  const leaves: Leave[] = leavesData?.leaves || [];
  const employees = employeesData?.employees || [];
  const systemSettings = settingsData?.systemSettings;

  // Determine if current user can approve leaves.
  // First gate: must have leaves.approve permission.
  // Then optionally restricted to designated approvers if system settings require it.
  const canApprove = useMemo(() => {
    if (!canApprovePerm) return false;

    if (
      systemSettings?.leaveApprovalRequired &&
      systemSettings.leaveApproverIds &&
      systemSettings.leaveApproverIds.length > 0
    ) {
      return currentUserId
        ? systemSettings.leaveApproverIds.includes(currentUserId)
        : false;
    }
    return true;
  }, [systemSettings, canApprovePerm, currentUserId]);

  // Filtered leaves
  const filteredLeaves = useMemo(() => {
    let result = [...leaves];

    // Filter by employee
    if (filterEmployeeIds.length > 0) {
      result = result.filter((leave) => filterEmployeeIds.includes(leave.employeeId));
    }

    // Filter by leave type
    if (filterLeaveTypes.length > 0) {
      result = result.filter((leave) => filterLeaveTypes.includes(leave.leaveType));
    }

    // Filter by status
    if (filterStatuses.length > 0) {
      result = result.filter((leave) => filterStatuses.includes(leave.status));
    }

    // Filter by date range
    if (filterDateFrom) {
      const fromDate = filterDateFrom + 'T00:00:00';
      result = result.filter((leave) => {
        const leaveEnd = new Date(leave.endDate).toISOString();
        return leaveEnd >= fromDate;
      });
    }

    if (filterDateTo) {
      const toDate = filterDateTo + 'T23:59:59';
      result = result.filter((leave) => {
        const leaveStart = new Date(leave.startDate).toISOString();
        return leaveStart <= toDate;
      });
    }

    // Sort by createdAt descending (most recent first)
    result.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return result;
  }, [leaves, filterEmployeeIds, filterLeaveTypes, filterStatuses, filterDateFrom, filterDateTo]);

  // Statistics
  const stats = useMemo(() => {
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const pendingCount = filteredLeaves.filter(
      (leave) => leave.status === 'PENDING'
    ).length;

    const approvedThisMonth = filteredLeaves.filter((leave) => {
      if (leave.status !== 'APPROVED') return false;
      const approvedDate = leave.approvedAt
        ? new Date(leave.approvedAt)
        : new Date(leave.createdAt);
      return (
        approvedDate.getMonth() === currentMonth &&
        approvedDate.getFullYear() === currentYear
      );
    }).length;

    const rejectedThisMonth = filteredLeaves.filter((leave) => {
      if (leave.status !== 'REJECTED') return false;
      const createdDate = new Date(leave.createdAt);
      return (
        createdDate.getMonth() === currentMonth &&
        createdDate.getFullYear() === currentYear
      );
    }).length;

    const allThisYear = filteredLeaves.filter((leave) => {
      const createdDate = new Date(leave.createdAt);
      return createdDate.getFullYear() === currentYear;
    }).length;

    return { pendingCount, approvedThisMonth, rejectedThisMonth, allThisYear };
  }, [filteredLeaves]);

  // Pagination
  const totalPages = Math.ceil(filteredLeaves.length / PAGE_SIZE);
  const paginatedLeaves = filteredLeaves.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterEmployeeIds, filterLeaveTypes, filterStatuses, filterDateFrom, filterDateTo]);

  // Handlers
  const handleCreateLeave = async (data: LeaveFormData) => {
    await createLeave({
      variables: {
        employeeId: data.employeeId,
        leaveType: data.leaveType,
        startDate: data.startDate + 'T00:00:00.000Z',
        endDate: data.endDate + 'T00:00:00.000Z',
        reason: data.reason || undefined,
      },
    });
  };

  const handleApproveLeave = async (leaveId: string) => {
    await updateLeave({
      variables: {
        id: leaveId,
        status: 'APPROVED',
        approvedBy: currentUserId,
      },
    });
    toast.success('Nieobecność została zatwierdzona');
    setShowDetailsDialog(false);
    setSelectedLeave(null);
  };

  const handleRejectLeave = async (leaveId: string) => {
    await updateLeave({
      variables: {
        id: leaveId,
        status: 'REJECTED',
      },
    });
    toast.success('Nieobecność została odrzucona');
    setShowDetailsDialog(false);
    setSelectedLeave(null);
  };

  const handleDeleteLeave = (leaveId: string) => {
    const leave = leaves.find((l) => l.id === leaveId) || null;
    setLeaveToDelete(leave);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteLeave = async () => {
    if (!leaveToDelete) return;
    await deleteLeave({
      variables: { id: leaveToDelete.id },
    });
    setShowDeleteConfirm(false);
    setLeaveToDelete(null);
    setShowDetailsDialog(false);
    setSelectedLeave(null);
  };

  const handleViewDetails = (leave: Leave) => {
    setSelectedLeave(leave);
    setShowDetailsDialog(true);
  };

  const handleEditLeave = (leave: Leave) => {
    setSelectedLeave(leave);
    setEditFormData({
      leaveType: leave.leaveType,
      startDate: new Date(leave.startDate).toISOString().split('T')[0],
      endDate: new Date(leave.endDate).toISOString().split('T')[0],
      status: leave.status,
      reason: leave.reason || '',
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedLeave) return;
    try {
      await updateLeave({
        variables: {
          id: selectedLeave.id,
          leaveType: editFormData.leaveType,
          startDate: editFormData.startDate + 'T00:00:00.000Z',
          endDate: editFormData.endDate + 'T00:00:00.000Z',
          status: editFormData.status,
          reason: editFormData.reason || undefined,
        },
      });
      toast.success('Nieobecność została zaktualizowana');
      setShowEditDialog(false);
      setSelectedLeave(null);
    } catch {
      // error handled by mutation onError
    }
  };

  // Sorted employees for dropdown
  const sortedEmployees = useMemo(() => {
    return [...employees].sort(
      (a: any, b: any) =>
        a.lastName.localeCompare(b.lastName, 'pl') ||
        a.firstName.localeCompare(b.firstName, 'pl')
    );
  }, [employees]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Ładowanie nieobecności...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-2">Błąd podczas ładowania nieobecności</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Umbrella className="w-8 h-8" />
            Nieobecności
          </h2>
          <p className="text-muted-foreground mt-2">
            Zarządzaj nieobecnościami i wnioskami
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Dodaj nieobecność</span>
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Oczekujące
                </p>
                <p className="text-2xl font-bold text-foreground">
                  {stats.pendingCount}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Zatwierdźone (miesiąc)
                </p>
                <p className="text-2xl font-bold text-foreground">
                  {stats.approvedThisMonth}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Odrzućone (miesiąc)
                </p>
                <p className="text-2xl font-bold text-foreground">
                  {stats.rejectedThisMonth}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                <XCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Wszystkie (rok)
                </p>
                <p className="text-2xl font-bold text-foreground">
                  {stats.allThisYear}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtry</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Employee filter */}
            <div className="space-y-2">
              <Label>Pracownik</Label>
              <MultiSelect
                options={sortedEmployees.map((emp: any) => ({
                  value: emp.id,
                  label: `${emp.lastName} ${emp.firstName}`,
                }))}
                selected={filterEmployeeIds}
                onChange={setFilterEmployeeIds}
                placeholder="Wszyscy pracownicy"
                searchable
                searchPlaceholder="Szukaj pracownika..."
              />
            </div>

            {/* Leave type filter */}
            <div className="space-y-2">
              <Label>Rodzaj</Label>
              <MultiSelect
                options={leaveTypes.map((type: { value: string; label: string }) => ({
                  value: type.value,
                  label: type.label,
                }))}
                selected={filterLeaveTypes}
                onChange={setFilterLeaveTypes}
                placeholder="Wszystkie typy"
              />
            </div>

            {/* Status filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <MultiSelect
                options={LEAVE_STATUS.map((status) => ({
                  value: status.value,
                  label: status.label,
                }))}
                selected={filterStatuses}
                onChange={setFilterStatuses}
                placeholder="Wszystkie statusy"
              />
            </div>

            {/* Date from */}
            <div className="space-y-2">
              <Label>Data od</Label>
              <Input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
              />
            </div>

            {/* Date to */}
            <div className="space-y-2">
              <Label>Data do</Label>
              <Input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                    Pracownik
                  </th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                    Dział
                  </th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                    Rodzaj
                  </th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                    Data od
                  </th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                    Data do
                  </th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                    Status
                  </th>
                  <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeaves.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center p-8 text-muted-foreground"
                    >
                      Brak nieobecności spełniających kryteria
                    </td>
                  </tr>
                ) : (
                  paginatedLeaves.map((leave) => {
                    const typeConfig = getLeaveTypeConfig(leave.leaveType);
                    const statusConfig = getStatusConfig(leave.status);

                    return (
                      <tr
                        key={leave.id}
                        className="border-b hover:bg-muted/50 transition-colors"
                      >
                        <td className="p-3">
                          <span className="font-medium">
                            {leave.employee.lastName} {leave.employee.firstName}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {leave.employee.department || '-'}
                        </td>
                        <td className="p-3">
                          <Badge className={typeConfig.color}>
                            {typeConfig.label}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm">
                          {new Date(leave.startDate).toLocaleDateString('pl-PL')}
                        </td>
                        <td className="p-3 text-sm">
                          {new Date(leave.endDate).toLocaleDateString('pl-PL')}
                        </td>
                        <td className="p-3">
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            {/* Details button - always visible */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(leave)}
                              title="Szczegóły"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>

                            {/* Approve button - only for PENDING and if user can approve */}
                            {leave.status === 'PENDING' && canApprove && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApproveLeave(leave.id)}
                                title="Zatwierdź"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}

                            {/* Reject button - only for PENDING and if user can approve */}
                            {leave.status === 'PENDING' && canApprove && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRejectLeave(leave.id)}
                                title="Odrzuć"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}

                            {/* Edit button — gated by leaves.approve */}
                            {canEditPerm && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditLeave(leave)}
                                title="Edytuj"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}

                            {/* Delete button — gated by leaves.delete */}
                            {canDeletePerm && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteLeave(leave.id)}
                                title="Usuń"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Strona {currentPage} z {totalPages} | Łącznie:{' '}
                {filteredLeaves.length} nieobecności
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Leave Dialog */}
      <LeaveDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSave={handleCreateLeave}
        employees={employees}
      />

      {/* Leave Details Dialog */}
      <LeaveDetailsDialog
        leave={selectedLeave}
        open={showDetailsDialog}
        onClose={() => {
          setShowDetailsDialog(false);
          setSelectedLeave(null);
        }}
        onApprove={
          canApprove && selectedLeave?.status === 'PENDING'
            ? handleApproveLeave
            : undefined
        }
        onReject={
          canApprove && selectedLeave?.status === 'PENDING'
            ? handleRejectLeave
            : undefined
        }
        onDelete={
          canDeletePerm ? handleDeleteLeave : undefined
        }
        loading={updateLoading || deleteLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={() => { setShowDeleteConfirm(false); setLeaveToDelete(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Potwierdzenie usunięcia</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Czy na pewno chcesz usunąć nieobecność pracownika{' '}
              <span className="font-medium text-foreground">
                {leaveToDelete?.employee.lastName} {leaveToDelete?.employee.firstName}
              </span>
              {' '}({leaveToDelete ? getLeaveTypeConfig(leaveToDelete.leaveType).label : ''}
              {', '}
              {leaveToDelete ? new Date(leaveToDelete.startDate).toLocaleDateString('pl-PL') : ''}
              {' - '}
              {leaveToDelete ? new Date(leaveToDelete.endDate).toLocaleDateString('pl-PL') : ''})?
            </p>
            <p className="text-sm text-red-600 mt-2">Tej operacji nie można cofnąć.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setLeaveToDelete(null); }} disabled={deleteLoading}>
              Anuluj
            </Button>
            <Button variant="destructive" onClick={confirmDeleteLeave} disabled={deleteLoading}>
              {deleteLoading ? 'Usuwanie...' : 'Usuń nieobecność'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Leave Dialog */}
      <Dialog open={showEditDialog} onOpenChange={() => { setShowEditDialog(false); setSelectedLeave(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edytuj nieobecność</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedLeave && (
                <div className="md:col-span-2">
                  <p className="text-sm text-muted-foreground">Pracownik</p>
                  <p className="font-medium">{selectedLeave.employee.lastName} {selectedLeave.employee.firstName}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Rodzaj</Label>
                <select
                  className="w-full p-2 border border-input rounded-md bg-background"
                  value={editFormData.leaveType}
                  onChange={(e) => setEditFormData({ ...editFormData, leaveType: e.target.value })}
                >
                  {leaveTypes.map((type: { value: string; label: string }) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <select
                  className="w-full p-2 border border-input rounded-md bg-background"
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                >
                  {LEAVE_STATUS.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Data początkowa</Label>
                <Input
                  type="date"
                  value={editFormData.startDate}
                  onChange={(e) => setEditFormData({ ...editFormData, startDate: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Data końcowa</Label>
                <Input
                  type="date"
                  value={editFormData.endDate}
                  onChange={(e) => setEditFormData({ ...editFormData, endDate: e.target.value })}
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label>Powód (opcjonalnie)</Label>
                <Input
                  placeholder="Powód nieobecności..."
                  value={editFormData.reason}
                  onChange={(e) => setEditFormData({ ...editFormData, reason: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setSelectedLeave(null); }} disabled={updateLoading}>
              Anuluj
            </Button>
            <Button onClick={handleSaveEdit} disabled={updateLoading}>
              {updateLoading ? 'Zapisywanie...' : 'Zapisz zmiany'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
